import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import useFetch from 'use-http';
import { IRootState } from 'src/reducers';
import selectors from 'src/selectors';
import UserDetails from 'src/models/UserDetails';
import Permissions from 'src/models/Permissions';
import Authorization from 'src/util/Authorization';
import {
    AlertTriangle, Building, Calendar, CheckCircle,
    List, Loader2, User, X, Printer 
} from 'src/components/common/icons';
import { ILeaseCheck, ILeaseCheckDetails } from 'src/models/dlcLease/ILeaseCheck';

interface ICheckDetailsModalProps {
    check: ILeaseCheck;
    onClose: () => void;
    currentUser?: UserDetails;
}

// --- Utility Functions ---
const formatCurrency = (amount: number | null) =>
    amount !== null ? `$${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}` : '-';

const formatDate = (dateString: string | null) =>
    dateString ? new Date(dateString).toLocaleDateString() : '-';

// --- Constants ---
const PRINTABLE_CHECK_TYPES = ['1', '2', '3', '4', '6', '7', '8'];

export const CheckDetailsModal: React.FC<ICheckDetailsModalProps> = ({ check, onClose, currentUser }) => {
    const [isVoided, setIsVoided] = useState(check.void === 'Y');
    const [voidReason, setVoidReason] = useState(check.voidReason);
    const isCleared = check.cleared === 'Y';
    const canVoidChecks = Authorization.HasPermission(Permissions.LeaseRecordsVoid, currentUser);

    const [lineItems, setLineItems] = useState<ILeaseCheckDetails[]>([]);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState<string | null>(null);

    // Void State
    const [isVoidDialogOpen, setIsVoidDialogOpen] = useState(false);
    const [voidReasonInput, setVoidReasonInput] = useState('');
    const [voidError, setVoidError] = useState<string | null>(null);

    // Print State
    const [hasPrintedSession, setHasPrintedSession] = useState(false);
    const [isPrinting, setIsPrinting] = useState(false); 

    // Standard hook usage
    const { get, post, response, loading } = useFetch();

    useEffect(() => {
        const fetchLineItems = async () => {
            if (!check.checkId) {
                return;
            }

            setLoadingDetails(true);
            setDetailsError(null);

            try {
                const data = await get(`/v1.0/leases/master/check-details/${check.checkId}`);
                if (response.ok) {
                    setLineItems(data);
                } else {
                    setDetailsError('Failed to load line items.');
                }
            } catch (err) {
                setDetailsError('Error connecting to server.');
            } finally {
                setLoadingDetails(false);
            }
        };

        fetchLineItems();
    }, [check.checkId, get, response]);

    const handleVoidClick = () => {
        setVoidReasonInput('');
        setVoidError(null);
        setIsVoidDialogOpen(true);
    };

    const submitVoidCheck = async () => {
        const normalizedReason = voidReasonInput.trim();
        if (!check.checkId || !normalizedReason || normalizedReason.length > 80) {
            return;
        }

        setVoidError(null);
        try {
            const result = await post('/v1.0/leases/master/void-check', {
                CheckId: check.checkId,
                Reason: normalizedReason
            });

            if (response.ok) {
                setIsVoided(true);
                setVoidReason(normalizedReason);
                setIsVoidDialogOpen(false);
            } else {
                setVoidError(typeof result === 'string' && result ? result : 'The check could not be voided.');
            }
        } catch (error) {
            setVoidError('The check could not be voided. Please try again.');
        }
    };

    const handlePrintClick = async () => {
        if (!check.checkId) {
            return;
        }

        setIsPrinting(true);

        try {
            await get(`/v1.0/leases/master/checks/${check.checkId}/print`);

            if (response.ok) {
                // Get the raw blob from the response object
                const blob = await response.blob(); 
                
                // Create Object URL for the PDF
                const fileUrl = window.URL.createObjectURL(blob);
                
                // Open in new tab
                window.open(fileUrl, '_blank');

                // SUCCESS: Enable the green checkmark
                setHasPrintedSession(true);
            } else {
                // Failed to download report
                // Optional: Check if response.data contains an error message
                // alert(response.data?.message || 'Failed to download check report.');
            }
        } catch (error) {
            // Error downloading report
            // alert('System error while downloading report.');
        } finally {
            setIsPrinting(false);
        }
    };

    const checkTypeId = String(check.checkType !== undefined ? check.checkType : check.type);
    const isPrintable = PRINTABLE_CHECK_TYPES.includes(checkTypeId);

    return (
        <div style={styles.detailsOverlay} onClick={onClose}>
            <div style={styles.detailsContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.detailsHeader}>
                    <div style={styles.detailsHeaderContent}>
                        <h3 style={styles.detailsTitle}>
                            Check Details
                            {isVoided && <span style={styles.headerVoidBadge}>VOID</span>}
                        </h3>
                        <div style={styles.detailsSubtitle}>Check Number #{check.checkNo}</div>
                    </div>
                    <button onClick={onClose} style={styles.iconCloseButton}>
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                {/* Body */}
                <div style={styles.detailsBody}>

                    {/* Highlights */}
                    <div style={styles.highlightsContainer}>
                        <div style={styles.highlightItem}>
                            <span style={styles.detailLabel}>Amount</span>
                            <span style={styles.highlightValue}>{formatCurrency(check.checkAmt)}</span>
                        </div>
                        <div style={styles.highlightItem}>
                            <span style={styles.detailLabel}>Check Date</span>
                            <div style={styles.commandValue}>
                                <Calendar style={styles.commandIcon} />
                                {formatDate(check.checkDate)}
                            </div>
                        </div>

                        <div style={styles.highlightItem}>
                            <span style={styles.detailLabel}>Cleared</span>
                            <div>
                                {isCleared ? (
                                    <span style={styles.modernBadgeCleared}>Yes</span>
                                ) : (
                                    <span style={styles.modernBadgePending}>No</span>
                                )}
                            </div>
                        </div>

                        <div style={styles.highlightItem}>
                            <span style={styles.detailLabel}>Voided</span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', position: 'relative' }}>
                                {isVoided ? (
                                    <span style={styles.modernBadgeVoid}>YES</span>
                                ) : (
                                    <>
                                        <span style={styles.modernBadgePending}>No</span>
                                        {canVoidChecks && (
                                            <button
                                                onClick={handleVoidClick}
                                                style={styles.voidActionButton}
                                            >
                                                Void Check
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Payee & Company */}
                    <div style={styles.sectionContainer}>
                        <div style={styles.detailsGridTwo}>
                            <div style={styles.infoCard}>
                                <h4 style={styles.cardHeader}>
                                    <User style={styles.cardHeaderIcon} /> Payee Information
                                </h4>
                                <div style={styles.cardContent}>
                                    <div style={styles.dataRow}>
                                        <span style={styles.dataLabel}>Payee:</span>
                                        <span style={styles.dataValueBold}>{check.payee || '-'}</span>
                                    </div>
                                    <div style={styles.dataRow}>
                                        <span style={styles.dataLabel}>Address:</span>
                                        <div style={styles.addressBlock}>
                                            <div>{check.addr1}</div>
                                            {check.addr2 && <div>{check.addr2}</div>}
                                            <div>{check.cstz}</div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style={styles.infoCard}>
                                <h4 style={styles.cardHeader}>
                                    <Building style={styles.cardHeaderIcon} /> Company Information
                                </h4>
                                <div style={styles.cardContent}>
                                    <div style={styles.dataRow}>
                                        <span style={styles.dataLabel}>Company:</span>
                                        <span style={styles.dataValue}>{check.company}</span>
                                    </div>
                                    <div style={styles.dataRow}>
                                        <span style={styles.dataLabel}>Memo:</span>
                                        <span style={styles.dataValueMemo}>{check.memoFor || '-'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Accounting */}
                    <div style={styles.sectionContainer}>
                        <h4 style={styles.sectionHeader}>Accounting & Tracking</h4>
                        <div style={styles.accountingGrid}>
                            <div style={styles.accountingItem}>
                                <span style={styles.detailLabel}>Type</span>
                                <span style={styles.accountingValue}>{check.type || check.checkType || '-'}</span>
                            </div>
                            <div style={styles.accountingItem}>
                                <span style={styles.detailLabel}>Date Sent</span>
                                <span style={styles.accountingValue}>{formatDate(check.sendDate)}</span>
                            </div>
                            <div style={styles.accountingItem}>
                                <span style={styles.detailLabel}>G/L Date</span>
                                <span style={styles.accountingValue}>{formatDate(check.glDate)}</span>
                            </div>
                            
                            {/* PRINTED SECTION with Button */}
                            <div style={styles.accountingItem}>
                                <span style={styles.detailLabel} />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>

                                    {/* Print Button */}
                                    {isPrintable && (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                            <button 
                                                onClick={handlePrintClick}
                                                style={{...styles.printButtonSmall, opacity: isPrinting ? 0.6 : 1}}
                                                title="Print Check PDF"
                                                disabled={isPrinting}
                                            >
                                                {isPrinting ? (
                                                    <Loader2 style={{ width: '1rem', height: '1rem', marginRight: '0.35rem', animation: 'spin 1s linear infinite' }} />
                                                ) : (
                                                    <Printer style={{ width: '1rem', height: '1rem', marginRight: '0.35rem' }} />
                                                )}
                                                {isPrinting ? 'Downloading...' : 'Print'}
                                            </button>
                                            
                                            {hasPrintedSession && !isPrinting && (
                                                <CheckCircle style={{ width: '1.25rem', height: '1.25rem', color: '#16a34a' }} />
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Void Section */}
                    {isVoided && (
                        <div style={styles.voidContainer}>
                            <h4 style={styles.voidHeader}>
                                <AlertTriangle style={styles.voidIcon} /> Void Details
                            </h4>
                            <div style={styles.voidGrid}>
                                <div>
                                    <span style={styles.voidLabel}>Void Date&nbsp;&nbsp;</span>
                                    <span style={styles.voidValue}>{formatDate(check.voidDate) !== '-' ? formatDate(check.voidDate) : 'Today'}</span>
                                </div>
                                <div>
                                    <span style={styles.voidLabel}>Reason&nbsp;&nbsp;</span>
                                    <span style={styles.voidValue}>{voidReason || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Line Items */}
                    <div style={styles.detailsFlexSection}>
                        <h4 style={styles.sectionHeader}>
                            <List style={styles.cardHeaderIcon} /> Line Item Details
                        </h4>

                        {loadingDetails && (
                            <div style={styles.detailsLoadingState}>
                                <Loader2 style={styles.loaderIconSmall} />
                                <span style={styles.loadingTextSmall}>Loading details...</span>
                            </div>
                        )}

                        {!loadingDetails && detailsError && (
                            <div style={styles.detailsErrorState}>{detailsError}</div>
                        )}

                        {!loadingDetails && !detailsError && (
                            <div style={styles.detailsTableWrapper}>
                                <table style={styles.detailsTable}>
                                    <thead>
                                        <tr>
                                            <th style={styles.miniHeader}>Lease No</th>
                                            <th style={styles.miniHeader}>Invoice No</th>
                                            <th style={styles.miniHeader}>Inv. Sub</th>
                                            <th style={styles.miniHeader}>Check For</th>
                                            <th style={styles.miniHeaderRight}>Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {lineItems.length > 0 ? (
                                            lineItems.map((item, idx) => (
                                                <tr key={idx} style={styles.miniRow}>
                                                    <td style={styles.miniCell}>
                                                        {item.lsNo ? (
                                                            <a target="_blank" rel="noreferrer" href={`/leases/${item.lsNo}`} style={styles.link}>{item.lsNo}</a>
                                                        ) : '-'}
                                                    </td>
                                                    <td style={styles.miniCell}>{item.invoiceNo}</td>
                                                    <td style={styles.miniCell}>{item.invoiceAdd || '-'}</td>
                                                    <td style={styles.miniCell}>{item.type || '-'}</td>
                                                    <td style={styles.miniCellRight}>{formatCurrency(item.amount)}</td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan={5} style={styles.miniCellEmpty}>No line items found.</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div style={styles.detailsFooter}>
                    <button onClick={onClose} style={styles.buttonClose}>Close</button>
                </div>
            </div>

            {/* Void Modal */}
            {canVoidChecks && isVoidDialogOpen && (
                <div style={styles.nestedOverlay} onClick={() => setIsVoidDialogOpen(false)}>
                    <div style={styles.nestedContent} onClick={e => e.stopPropagation()}>
                        <div style={styles.nestedHeader}>Void Check</div>
                        <div style={styles.nestedBody}>
                            <label style={styles.label}>Void Reason</label>
                            <textarea
                                rows={5}
                                value={voidReasonInput}
                                onChange={(e) => setVoidReasonInput(e.target.value)}
                                style={styles.textarea}
                                placeholder="Enter reason..."
                                maxLength={80}
                            />
                            <div style={styles.characterCount}>{voidReasonInput.length}/80</div>
                            {voidError && <div style={styles.voidError}>{voidError}</div>}
                        </div>
                        <div style={styles.nestedFooter}>
                            <button
                                onClick={() => setIsVoidDialogOpen(false)}
                                style={styles.buttonCancel}
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={submitVoidCheck}
                                style={styles.buttonVoidConfirm}
                                disabled={loading || !voidReasonInput.trim() || voidReasonInput.trim().length > 80}
                            >
                                {loading ? <Loader2 style={{ width: 16, height: 16, animation: 'spin 1s linear infinite' }} /> : 'Void Check'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    // ... (Keep existing styles) ...
    detailsOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 60, padding: '1.5rem', backdropFilter: 'blur(2px)',
    },
    detailsContent: {
        backgroundColor: '#FFFFFF', borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '100%', maxWidth: '900px', maxHeight: '90vh',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    },
    detailsHeader: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'start',
        padding: '1rem 1.5rem', backgroundColor: '#fff', borderBottom: '1px solid #f1f5f9',
    },
    detailsHeaderContent: { display: 'flex', flexDirection: 'column' },
    detailsTitle: { fontSize: '1.75rem', fontWeight: 800, color: '#0f172a', margin: 0, lineHeight: 1.2, letterSpacing: '-0.025em', display: 'flex', alignItems: 'center', gap: '0.75rem' },
    detailsSubtitle: { fontSize: '0.95rem', color: '#64748b', marginTop: '0.25rem', fontWeight: 500 },
    headerVoidBadge: {
        backgroundColor: '#FEE2E2', color: '#DC2626', padding: '0.25rem 0.5rem', borderRadius: '0.375rem',
        fontSize: '0.875rem', fontWeight: 700, border: '1px solid #FECACA', letterSpacing: '0.05em'
    },
    iconCloseButton: {
        padding: '0.5rem', borderRadius: '50%', backgroundColor: '#f1f5f9', border: 'none',
        cursor: 'pointer', color: '#64748b', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    closeIcon: { width: '1.5rem', height: '1.5rem' },
    detailsBody: {
        padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem',
        overflow: 'hidden', flex: '1 1 auto',
    },
    highlightsContainer: {
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '0.75rem', border: '1px solid #e2e8f0',
    },
    highlightItem: { display: 'flex', flexDirection: 'column', gap: '0.5rem', flex: 1 },
    detailLabel: { fontSize: '0.75rem', textTransform: 'uppercase', color: '#64748b', fontWeight: 700, letterSpacing: '0.05em' },
    highlightValue: { fontSize: '2rem', fontWeight: 800, color: '#0f172a', lineHeight: 1 },
    commandValue: { fontSize: '1rem', fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center' },
    commandIcon: { width: '1rem', height: '1rem', marginRight: '0.375rem', color: '#64748b' },

    // Status Badges
    modernBadgeVoid: {
        backgroundColor: '#FEF2F2', color: '#DC2626', padding: '0.25rem 0.5rem', borderRadius: '9999px',
        fontSize: '0.75rem', fontWeight: 800, border: '1px solid #FECACA', textTransform: 'uppercase', letterSpacing: '0.05em',
    },
    modernBadgeCleared: {
        backgroundColor: '#f0fdf4', color: '#166534', padding: '0.25rem 0.5rem', borderRadius: '9999px',
        fontSize: '0.75rem', fontWeight: 700, border: '1px solid #bbf7d0', textTransform: 'uppercase', letterSpacing: '0.05em',
    },
    modernBadgePending: {
        backgroundColor: '#f1f5f9', color: '#475569', padding: '0.25rem 0.5rem', borderRadius: '9999px',
        fontSize: '0.75rem', fontWeight: 700, border: '1px solid #e2e8f0', textTransform: 'uppercase', letterSpacing: '0.05em',
    },
    voidActionButton: {
        backgroundColor: '#FFFFFF', border: '1px solid #EF4444', color: '#EF4444',
        borderRadius: '6px', padding: '0.15rem 0.6rem', fontSize: '0.75rem', fontWeight: 600,
        cursor: 'pointer', transition: 'all 0.2s', textTransform: 'uppercase'
    },
    
    // Updated button style for inside the grid
    printButtonSmall: {
        backgroundColor: '#EFF6FF', border: '1px solid #BFDBFE', color: '#2563EB',
        borderRadius: '4px', padding: '0.25rem 0.6rem', fontSize: '0.75rem', fontWeight: 600,
        cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center',
        marginLeft: '0.5rem'
    },
    infoButton: {
        background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center'
    },

    sectionContainer: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
    sectionHeader: { fontSize: '1rem', fontWeight: 700, color: '#0f172a', margin: 0, display: 'flex', alignItems: 'center' },
    detailsGridTwo: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' },
    infoCard: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
    cardHeader: { fontSize: '0.875rem', fontWeight: 700, color: '#334155', margin: 0, display: 'flex', alignItems: 'center' },
    cardHeaderIcon: { width: '1rem', height: '1rem', marginRight: '0.5rem', color: '#64748b' },
    cardContent: {
        display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '1rem',
        backgroundColor: '#fff', border: '1px solid #e2e8f0', borderRadius: '0.75rem',
    },
    dataRow: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
    dataLabel: { fontSize: '0.75rem', fontWeight: 600, color: '#64748b' },
    dataValue: { fontSize: '0.95rem', fontWeight: 500, color: '#0f172a' },
    dataValueBold: { fontSize: '1rem', fontWeight: 700, color: '#0f172a' },
    dataValueMemo: { fontSize: '0.95rem', fontStyle: 'italic', color: '#334155' },
    addressBlock: { display: 'flex', flexDirection: 'column', fontSize: '0.95rem', color: '#334155', lineHeight: 1.5 },
    accountingGrid: {
        display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', padding: '1rem',
        backgroundColor: '#f8fafc', borderRadius: '0.75rem', border: '1px solid #e2e8f0',
    },
    accountingItem: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
    accountingValue: { fontSize: '0.95rem', fontWeight: 600, color: '#0f172a' },
    checkboxWrapper: { display: 'flex', alignItems: 'center', gap: '0.5rem' },
    checkedIcon: { width: '1.25rem', height: '1.25rem', color: '#166534' },
    uncheckedIcon: { width: '1.25rem', height: '1.25rem', color: '#cbd5e1' },
    checkboxLabel: { fontSize: '0.95rem', fontWeight: 500, color: '#334155' },
    voidContainer: {
        backgroundColor: '#fef2f2', padding: '1rem', borderRadius: '0.75rem',
        border: '1px solid #fecaca', display: 'flex', flexDirection: 'column', gap: '1rem',
    },
    voidHeader: { fontSize: '1rem', fontWeight: 700, color: '#991b1b', margin: 0, display: 'flex', alignItems: 'center' },
    voidIcon: { width: '1.25rem', height: '1.25rem', marginRight: '0.5rem' },
    voidGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' },
    voidLabel: { fontSize: '0.75rem', fontWeight: 600, color: '#b91c1c' },
    voidValue: { fontSize: '0.95rem', fontWeight: 600, color: '#7f1d1d' },
    detailsFlexSection: { display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: '1 1 auto', minHeight: '0' },
    detailsLoadingState: {
        display: 'flex', alignItems: 'center', padding: '1.5rem',
        backgroundColor: '#f9fafb', borderRadius: '0.5rem', color: '#64748b', fontSize: '0.875rem',
    },
    loaderIconSmall: { width: '1.25rem', height: '1.25rem', marginRight: '0.5rem', animation: 'spin 1s linear infinite', color: '#4f46e5' },
    loadingTextSmall: { fontWeight: 500 },
    detailsErrorState: { padding: '1.5rem', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '0.5rem', fontSize: '0.875rem', fontWeight: 500 },
    detailsTableWrapper: { overflowY: 'auto', maxHeight: '300px', borderRadius: '0.5rem', border: '1px solid #e2e8f0', minHeight: '100px' },
    detailsTable: { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' },
    miniHeader: {
        padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: 700,
        textTransform: 'uppercase', color: '#64748b', backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0', letterSpacing: '0.025em', position: 'sticky', top: 0, zIndex: 10,
    },
    miniHeaderRight: {
        padding: '0.75rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: 700,
        textTransform: 'uppercase', color: '#64748b', backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0', letterSpacing: '0.025em', position: 'sticky', top: 0, zIndex: 10,
    },
    miniRow: { borderBottom: '1px solid #f1f5f9' },
    miniCell: { padding: '0.75rem', color: '#334155' },
    miniCellRight: { padding: '0.75rem', color: '#0f172a', textAlign: 'right', fontWeight: 600 },
    miniCellEmpty: { padding: '1.5rem', color: '#64748b', textAlign: 'center', fontStyle: 'italic' },
    detailsFooter: { padding: '1.5rem 2rem', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#fff' },
    buttonClose: {
        padding: '0.625rem 1.25rem', border: '1px solid #cbd5e1', borderRadius: '0.5rem',
        backgroundColor: '#fff', color: '#0f172a', cursor: 'pointer', fontSize: '0.875rem',
        fontWeight: 600, transition: 'all 0.2s', boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    },
    link: { color: '#4F46E5', textDecoration: 'underline', fontWeight: 500, cursor: 'pointer' },

    nestedOverlay: {
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        zIndex: 70, backdropFilter: 'blur(1px)',
    },
    nestedContent: {
        backgroundColor: '#FFFFFF', borderRadius: '0.75rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
        width: '350px', display: 'flex', flexDirection: 'column',
    },
    nestedHeader: {
        padding: '1rem', borderBottom: '1px solid #E5E7EB', fontWeight: 700, fontSize: '1.1rem', color: '#111827'
    },
    nestedBody: { padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' },
    label: { fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.25rem' },
    textarea: {
        padding: '0.5rem', border: '1px solid #D1D5DB', borderRadius: '0.375rem',
        fontSize: '0.875rem', outline: 'none', width: '100%', color: '#111827',
        resize: 'vertical', fontFamily: 'inherit',
    },
    characterCount: {
        marginTop: '0.25rem', textAlign: 'right', fontSize: '0.75rem', color: '#64748b',
    },
    voidError: {
        marginTop: '0.5rem', color: '#b91c1c', fontSize: '0.875rem', fontWeight: 500,
    },
    nestedFooter: {
        padding: '1rem', borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', backgroundColor: '#F9FAFB', borderRadius: '0 0 0.75rem 0.75rem'
    },
    buttonCancel: {
        padding: '0.5rem 1rem', border: '1px solid #D1D5DB', borderRadius: '0.375rem',
        backgroundColor: '#FFFFFF', color: '#374151', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500
    },
    buttonVoidConfirm: {
        padding: '0.5rem 1rem', border: 'none', borderRadius: '0.375rem',
        backgroundColor: '#DC2626', color: '#FFFFFF', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: '0.5rem'
    }
};

const mapStateToProps = (state: IRootState) => ({
    currentUser: selectors.auth.user(state),
});

export default connect(mapStateToProps)(CheckDetailsModal);



// WEBPACK FOOTER //
// ./src/components/invoicing/components/CheckDetailsModal.tsx